import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { PERMISSIONS } from "../utils/permissions";

interface OrderNotification {
  id: string;
  orderNumber: string;
  total: number;
  currency: string;
  customerName: string | null;
  createdAt: string;
}

interface OrderNotificationsContextType {
  pendingOrdersCount: number;
}

const OrderNotificationsContext = createContext<
  OrderNotificationsContextType | undefined
>(undefined);

const getStreamUrl = (token: string) => {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL?.replace(
    /\/$/,
    "",
  );

  if (!configuredBaseUrl) return null;

  const apiBaseUrl = configuredBaseUrl.endsWith("/api")
    ? configuredBaseUrl
    : `${configuredBaseUrl}/api`;

  return `${apiBaseUrl}/orders/notifications/stream?token=${encodeURIComponent(
    token,
  )}`;
};

const getStoredToken = () =>
  localStorage.getItem("authToken") || localStorage.getItem("token");

const isJwtExpired = (token: string) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return false;

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = `${normalizedPayload}${"=".repeat(
      (4 - (normalizedPayload.length % 4)) % 4,
    )}`;
    const decodedPayload = JSON.parse(atob(paddedPayload));

    return (
      typeof decodedPayload.exp === "number" &&
      decodedPayload.exp * 1000 <= Date.now()
    );
  } catch {
    return false;
  }
};

const getCount = (value: unknown) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
};

export const OrderNotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isAuthenticated, isLoading, token, user, hasPermission } = useAuth();
  const { info } = useToast();
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const seenOrderIds = useRef(new Set<string>());

  const canReadOrders =
    isAuthenticated &&
    user?.role !== "customer" &&
    hasPermission(PERMISSIONS.VIEW_ORDERS);

  useEffect(() => {
    if (
      isLoading ||
      !canReadOrders ||
      typeof window === "undefined" ||
      !("EventSource" in window)
    ) {
      setPendingOrdersCount(0);
      return;
    }

    let eventSource: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let reconnectAttempts = 0;
    let stopped = false;

    const handleCount = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data);
        const count = getCount(payload.count);
        if (count !== null) setPendingOrdersCount(count);
      } catch {
        // Ignore malformed stream events and keep the last known count.
      }
    };

    const handleNewOrder = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as {
          count?: unknown;
          order?: OrderNotification;
        };
        const count = getCount(payload.count);
        if (count !== null) setPendingOrdersCount(count);

        const order = payload.order;
        if (!order?.id || seenOrderIds.current.has(order.id)) return;

        seenOrderIds.current.add(order.id);
        if (seenOrderIds.current.size > 100) {
          const firstOrderId = seenOrderIds.current.values().next().value;
          if (firstOrderId) seenOrderIds.current.delete(firstOrderId);
        }

        const total = Number(order.total);
        const formattedTotal = Number.isFinite(total) ? total.toFixed(2) : "0.00";
        info(
          `New order ${order.orderNumber || order.id} - ${
            order.currency || "QAR"
          } ${formattedTotal}`,
        );
      } catch {
        // Ignore malformed stream events and keep the connection active.
      }
    };

    const closeStream = () => {
      if (!eventSource) return;

      eventSource.removeEventListener("count", handleCount);
      eventSource.removeEventListener("new_order", handleNewOrder);
      eventSource.onopen = null;
      eventSource.onerror = null;
      eventSource.close();
      eventSource = null;
    };

    const clearReconnectTimer = () => {
      if (reconnectTimer !== undefined) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
    };

    const connect = () => {
      clearReconnectTimer();
      if (stopped) return;

      const currentToken = getStoredToken();
      if (!currentToken || isJwtExpired(currentToken)) return;

      const streamUrl = getStreamUrl(currentToken);
      if (!streamUrl) return;

      closeStream();
      const stream = new EventSource(streamUrl);
      eventSource = stream;

      stream.addEventListener("count", handleCount);
      stream.addEventListener("new_order", handleNewOrder);
      stream.onopen = () => {
        reconnectAttempts = 0;
      };
      stream.onerror = () => {
        if (stopped || eventSource !== stream) return;

        // Rebuild the URL from storage so a rotated JWT is used on the next stream.
        closeStream();
        const freshToken = getStoredToken();
        if (!freshToken || isJwtExpired(freshToken)) return;

        const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);
        reconnectAttempts += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    const reconnectWithFreshToken = () => {
      reconnectAttempts = 0;
      closeStream();
      connect();
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "authToken" || event.key === "token") {
        reconnectWithFreshToken();
      }
    };

    window.addEventListener("authChanged", reconnectWithFreshToken);
    window.addEventListener("storage", handleStorageChange);
    connect();

    return () => {
      stopped = true;
      clearReconnectTimer();
      closeStream();
      window.removeEventListener("authChanged", reconnectWithFreshToken);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [canReadOrders, info, isLoading, token]);

  const value = useMemo(
    () => ({ pendingOrdersCount }),
    [pendingOrdersCount],
  );

  return (
    <OrderNotificationsContext.Provider value={value}>
      {children}
    </OrderNotificationsContext.Provider>
  );
};

export const useOrderNotifications = () => {
  const context = useContext(OrderNotificationsContext);
  if (!context) {
    throw new Error(
      "useOrderNotifications must be used within OrderNotificationsProvider",
    );
  }
  return context;
};
